// src/components/sync/ProjectSyncStartButton.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Group, Loader, Modal, Progress, Stack, Text, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { FaCheck, FaPlay, FaSyncAlt, FaTimes } from "react-icons/fa";
import type { IProjectRun } from "@backend/src/shared/schemas/schemas";
import { useGetProjectSyncRunsQuery, usePostMultiplacerSyncStartMutation } from "../../openapi";

type Props = {
  projectNo: number;
  /** Hvor mange siste runs som hentes (default 1) */
  limit?: number;
};

function formatDateTime(dt: string | Date | undefined): string {
  if (!dt) return "";
  const d = dt instanceof Date ? dt : new Date(dt);
  if (Number.isNaN(d.getTime())) return String(dt);
  return d.toLocaleString();
}

function labelForRunStatus(status: IProjectRun["status"]) {
  switch (status) {
    case "succeeded":
      return "Vellykket";
    case "failed":
      return "Mislykket";
    case "running":
    default:
      return "Pågår";
  }
}

function colorForRunStatus(status: IProjectRun["status"]) {
  switch (status) {
    case "succeeded":
      return "green";
    case "failed":
      return "red";
    case "running":
    default:
      return "blue";
  }
}

function safePercent(done: number, total: number) {
  if (total <= 0) return 0;
  const p = (done / total) * 100;
  return Math.max(0, Math.min(100, p));
}

type StartUiState = "idle" | "sending" | "started" | "error";

export const ProjectSyncStartButton: React.FC<Props> = ({ projectNo, limit = 1 }) => {
  const [pollMs, setPollMs] = useState<number>(0);
  const [confirmOpen, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);

  // Lite "toast-ish" modal som vises etter at request er sendt
  const [startUi, setStartUi] = useState<StartUiState>("idle");
  const [startInfoOpen, { open: openStartInfo, close: closeStartInfo }] = useDisclosure(false);

  const {
    data: runsData,
    isLoading,
    isFetching,
    refetch,
    isError,
    error,
  } = useGetProjectSyncRunsQuery(
    { projectNo, limit },
    { refetchOnMountOrArgChange: true, pollingInterval: pollMs }
  );

  const latestRun: IProjectRun | undefined = useMemo(() => runsData?.items?.[0], [runsData]);
  const isRunning = latestRun?.status === "running";

  useEffect(() => {
    setPollMs(isRunning ? 5000 : 0);
  }, [isRunning]);

  const [startSync, { isLoading: isStarting }] = usePostMultiplacerSyncStartMutation();

  const handleStart = async () => {
    // Lukk bekreftelse-modal umiddelbart (etter "send")
    closeConfirm();

    // Vis "sender..." modal
    setStartUi("sending");
    openStartInfo();

    try {
      await startSync({
        body: {
          projectNo,
          jobTypes: ["addIfcInfo", "specifiedArticles", "standardRoom.E", "standardRoom.V"],
        },
      }).unwrap();

      // Request svarte -> vis hyggelig "i gang"
      setStartUi("started");

      notifications.show({
        title: "Startet",
        message: "Synk og beriking er startet.",
        color: "green",
        icon: <FaCheck size={16} />,
      });

      // Trigger en rask refresh (men ikke vent med UI)
      void refetch();

      // Lukk den lille info-modalen etter en kort stund (uten å blokkere)
      window.setTimeout(() => {
        closeStartInfo();
        setStartUi("idle");
      }, 1400);
    } catch (e) {
      setStartUi("error");

      notifications.show({
        title: "Feil",
        message: "Kunne ikke starte synk.",
        color: "red",
        icon: <FaTimes size={16} />,
      });

      // La modal stå åpen slik at brukeren ser feilen og kan lukke selv
      // (ev. kan du auto-lukke etter noen sekunder hvis du vil)
      // window.setTimeout(() => closeStartInfo(), 2500);
    }
  };

  // --- UI states -------------------------------------------------------------

  if (isLoading) {
    return (
      <Group gap="xs">
        <Loader size="xs" />
        <Text size="sm" c="dimmed">
          Laster…
        </Text>
      </Group>
    );
  }

  if (isError) {
    return (
      <Group gap="xs">
        <Badge color="red" variant="filled">
          Feil
        </Badge>
        <Button
          size="xs"
          variant="light"
          leftSection={<FaSyncAlt size={14} />}
          onClick={() => refetch()}
        >
          Prøv igjen
        </Button>
        <Text size="xs" c="dimmed">
          {String((error as any)?.data?.message ?? (error as any)?.error ?? "")}
        </Text>
      </Group>
    );
  }

  // When running: show status panel + refresh
  if (isRunning && latestRun) {
    const enrichStep = latestRun.steps?.enrich;
    const done = typeof enrichStep?.done === "number" ? enrichStep.done : 0;
    const failed = typeof enrichStep?.failed === "number" ? enrichStep.failed : 0;
    const total = typeof enrichStep?.total === "number" ? enrichStep.total : 0;

    const percent = safePercent(done + failed, total);

    return (
      <Stack
        w="100%"
        gap="xs"
        p="sm"
        style={{
          background: "var(--mantine-color-gray-0)",
          borderRadius: 12,
        }}
      >
        <Group justify="space-between" align="center">
          <Group gap="xs">
            <Badge color={colorForRunStatus(latestRun.status)} variant="filled">
              {labelForRunStatus(latestRun.status)}
            </Badge>
            <Text size="sm" fw={600}>
              Synk kjører
            </Text>
            {isFetching && <Loader size="xs" />}
          </Group>

          <Tooltip label="Oppdater status" withArrow>
            <Button
              variant="subtle"
              size="xs"
              leftSection={<FaSyncAlt size={14} />}
              onClick={() => refetch()}
            >
              Oppdater
            </Button>
          </Tooltip>
        </Group>

        <Text size="xs" c="dimmed">
          Startet: {formatDateTime(latestRun.startedAt ?? latestRun.createdAt)}
        </Text>

        {total > 0 ? (
          <Stack gap={6}>
            <Progress value={percent} size="lg" radius="xl" striped animated />
            <Group justify="space-between" align="center">
              <Group gap="sm" align="center">
                <Text size="md" fw={700}>
                  {done + failed} / {total}
                </Text>
                {failed > 0 && (
                  <Badge variant="filled" color="red">
                    FEIL: {failed}
                  </Badge>
                )}
              </Group>
              <Text size="sm" c="dimmed">
                Pågår…
              </Text>
            </Group>
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">
            Pågår… (venter på fremdrift)
          </Text>
        )}
      </Stack>
    );
  }

  // Not running: show start button + confirm modal + last result (optional)
  return (
    <>
      <Group gap="sm" align="center">
        <Button
          leftSection={<FaPlay size={16} />}
          size="xs"
          px="xs"
          radius="sm"
          onClick={openConfirm}
          variant="filled"
          color="blue"
          loading={isStarting}
          disabled={Boolean(isRunning)}
        >
          Start synk og beriking
        </Button>

        <Tooltip label="Oppdater" withArrow>
          <Button
            variant="subtle"
            size="xs"
            leftSection={<FaSyncAlt size={14} />}
            onClick={() => refetch()}
            loading={isFetching}
          >
            Oppdater
          </Button>
        </Tooltip>

        {latestRun && (
          <Badge color={colorForRunStatus(latestRun.status)} variant="light">
            Sist: {labelForRunStatus(latestRun.status)} ({formatDateTime(latestRun.createdAt)})
          </Badge>
        )}
      </Group>

      {/* Confirm modal */}
      <Modal opened={confirmOpen} onClose={closeConfirm} title="Start synk?" centered radius="md">
        <Stack gap="md">
          <Text size="sm">
            Vil du starte synk og beriking for prosjekt{" "}
            <Text span fw={700}>
              {projectNo}
            </Text>
            ?
          </Text>

          <Group justify="flex-end" gap="sm">
            <Button variant="outline" onClick={closeConfirm} disabled={isStarting}>
              Nei
            </Button>
            <Button leftSection={<FaPlay size={16} />} onClick={handleStart} loading={isStarting}>
              Ja, start nå
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Start info modal (spinner -> success/error) */}
      <Modal
        opened={startInfoOpen}
        onClose={() => {
          closeStartInfo();
          setStartUi("idle");
        }}
        withCloseButton={startUi !== "sending"}
        closeOnClickOutside={startUi !== "sending"}
        closeOnEscape={startUi !== "sending"}
        centered
        radius="md"
        title={startUi === "sending" ? "Starter…" : startUi === "started" ? "I gang" : "Feil"}
      >
        <Stack gap="sm">
          {startUi === "sending" && (
            <>
              <Group gap="sm" align="center">
                <Loader size="sm" />
                <Text size="sm" fw={600}>
                  Sender forespørsel…
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                Dette tar normalt bare et øyeblikk.
              </Text>
            </>
          )}

          {startUi === "started" && (
            <>
              <Group gap="sm" align="center">
                <FaCheck size={16} />
                <Text size="sm" fw={700}>
                  Synk er startet
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                Du kan lukke dette vinduet – status oppdateres automatisk i panelet.
              </Text>
            </>
          )}

          {startUi === "error" && (
            <>
              <Group gap="sm" align="center">
                <FaTimes size={16} />
                <Text size="sm" fw={700} c="red">
                  Kunne ikke starte synk
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                Prøv igjen om litt, eller sjekk at ingen annen kjøring allerede pågår.
              </Text>

              <Group justify="flex-end" gap="sm" mt="xs">
                <Button variant="outline" onClick={() => closeStartInfo()}>
                  Lukk
                </Button>
                <Button
                  leftSection={<FaPlay size={16} />}
                  onClick={handleStart}
                  loading={isStarting}
                >
                  Prøv igjen
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </>
  );
};
